import useGetUserInfo from "@/hooks/queries/useGetUserInfo";
import useAuth from "@/hooks/useAuth";
import AiIconHeader from "@/icons/aiIconHeader";
import { recoverTokenInfo } from "@/utils/recoverTokenInfo";
import {
  ActionMenu,
  Button,
  ButtonVariant,
  ChevronDownIcon,
  ChevronUpIcon,
  Color,
  GearIcon,
  HorizontalFlex,
  IconSize,
  LogoutIcon,
  MediumTextM,
  ModalIcon,
  OwnerIcon,
  Space,
  VerticalSeparator,
} from "@enxcs/dap-ui-stratos";
import { jwtDecode } from "jwt-decode";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductFruits } from "react-product-fruits";
import { useNavigate } from "react-router-dom";
import SelectLanguage from "../SelectLanguage";
import { CustomHeaderContainer } from "./Header.styles";
import HeaderLogo from "./HeaderLogo";

type JwtPayload = {
  unique_name?: string;
  Fullname?: string;
  [key: string]: unknown;
};

const HeaderSmartDebrief: FC = () => {
  const { i18n } = useTranslation();
  const [productFruitsInfo, setProductFruitsInfo] = useState({ username: "" });
  const ibabsUserId = useMemo(() => recoverTokenInfo("ibabs-user-id") || "", []);

  const currentLang = i18n.language;

  const { token, logOut } = useAuth();

  const { t } = useTranslation();

  const navigate = useNavigate();

  const { data: iBabsUserInfo, isLoadingUserInfo } = useGetUserInfo();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuTriggerRef = useRef<HTMLButtonElement>(null);

  // Get user name from iBabs or jwt token
  const userName = useMemo(() => {
    // First, try to get name from iBabsUserInfo
    //   (ensure it is not a string and has the expected properties)
    if (iBabsUserInfo && typeof iBabsUserInfo !== "string" && "firstName" in iBabsUserInfo && "lastName" in iBabsUserInfo && !isLoadingUserInfo) {
      const info = iBabsUserInfo as { firstName?: string; lastName?: string };
      const { firstName, lastName } = info;
      return `${firstName} ${lastName}`;
    } else {
      // Fallback with JWT token
      if (token) {
        try {
          const decoded = jwtDecode<JwtPayload>(token);
          return decoded?.unique_name || decoded?.Fullname || t("recordsLibrary.userNamePlaceholder");
        } catch {
          return t("recordsLibrary.userNamePlaceholder");
        }
      }
    }

    return t("recordsLibrary.userNamePlaceholder");
  }, [iBabsUserInfo, isLoadingUserInfo, t, token]);

  const handleLogout = () => {
    logOut();
  };

  // Forces new language to Product Fruits when language changes
  const productFruitsLanguage = useMemo(() => {
    switch (currentLang) {
      case "nl":
        return "nl";
      case "fr":
        return "en"; // No French support, fallback to English
      default:
        return "en";
    }
  }, [currentLang]);

  useEffect(() => {
    if (currentLang && !!ibabsUserId) setProductFruitsInfo({ username: ibabsUserId });
  }, [currentLang, ibabsUserId]);

  return (
    <CustomHeaderContainer id={"layout-header"} $justify="space-between" $padLeft={Space.V24} $padRight={Space.V24}>
      {productFruitsInfo.username !== "" && currentLang && productFruitsLanguage && (
        <ProductFruits
          key={`${currentLang}-${ibabsUserId}`}
          workspaceCode={import.meta.env.VITE_PRODUCT_FRUITS}
          language={productFruitsLanguage}
          user={productFruitsInfo}
          debug={true}
          lifeCycle="unmount"
        />
      )}

      <HorizontalFlex $gapX={Space.V12}>
        <HeaderLogo onLogoClick={() => navigate(`/${i18n.language}`)} />

        <VerticalSeparator />

        <MediumTextM>{t("appName")}</MediumTextM>

        <VerticalSeparator />

        <HorizontalFlex $gap={Space.V2}>
          <AiIconHeader />
        </HorizontalFlex>
      </HorizontalFlex>

      <HorizontalFlex $gapX={Space.V12}>
        <Button
          id="header-user-menu-button"
          data-qa-id="header-user-menu-button"
          label={userName}
          leftIcon={() => <OwnerIcon size={IconSize.V24} tone={Color.Teal.V40} />}
          rightIcon={() => {
            if (!isUserMenuOpen) return <ChevronDownIcon size={IconSize.V18} tone={Color.Teal.V40} />;
            return <ChevronUpIcon size={IconSize.V18} tone={Color.Teal.V40} />;
          }}
          elementRef={userMenuTriggerRef}
          ariaExpanded={isUserMenuOpen}
          ariaControls="header-user-menu"
          ariaHaspopup="menu"
          onClick={() => setIsUserMenuOpen((prev) => !prev)}
          variant={ButtonVariant.GHOST}
        />

        {isUserMenuOpen && (
          <ActionMenu
            options={[
              {
                icon: () => <OwnerIcon size={IconSize.V24} tone={Color.Teal.V40} />,
                iconTone: Color.Teal.V40,
                label: userName,
                onClick: () => {},
                subLabel: `${t("buttons.site")}: ${recoverTokenInfo("ibabs-site-name") || "-"}`,
              },
              {
                icon: () => <GearIcon size={IconSize.V24} tone={Color.Teal.V40} />,
                label: t("buttons.setting"),
                onClick: () => {
                  navigate(`/${i18n.language}/settings`);
                },
              },
              {
                icon: () => <ModalIcon size={IconSize.V24} tone={Color.Teal.V40} />,
                label: t("buttons.backToIBabs"),
                onClick: () => {
                  const redirectURL = import.meta.env.VITE_IBABS_PORTAL_URL || "https://portal.ibabs.eu";
                  window.open(redirectURL, "_blank");
                },
              },
              {
                icon: () => <LogoutIcon size={IconSize.V24} tone={Color.Teal.V40} />,
                label: t("buttons.logout"),
                onClick: () => {
                  handleLogout();
                },
              },
            ]}
            triggerRef={userMenuTriggerRef}
            id="header-user-menu"
            onClose={() => setIsUserMenuOpen(false)}
          />
        )}

        <SelectLanguage />
      </HorizontalFlex>
    </CustomHeaderContainer>
  );
};

export default HeaderSmartDebrief;
